import { useState } from "react";
import { cn } from "@/lib/utils";
import { APPLICABILITY_OPTIONS, EVIDENCE_ANSWERS, EVIDENCE_CONDITION_VALUES, FIELD_VERIFICATION_OPTIONS, FIELD_VERIFICATION_LABELS } from "@/lib/questionnaire";

const CONDITION_STATE = {
  current: "data-[selected=true]:bg-success-soft data-[selected=true]:text-success data-[selected=true]:border-success/40",
  review: "data-[selected=true]:bg-warning-soft data-[selected=true]:text-warning data-[selected=true]:border-warning/40",
  missing: "data-[selected=true]:bg-destructive-soft data-[selected=true]:text-destructive data-[selected=true]:border-destructive/40"
};

const APPLICABILITY_STATE = {
  applicable: "data-[selected=true]:bg-info-soft data-[selected=true]:text-info data-[selected=true]:border-info/40",
  unsure: "data-[selected=true]:bg-destructive-soft data-[selected=true]:text-destructive data-[selected=true]:border-destructive/40",
  na: "data-[selected=true]:bg-secondary data-[selected=true]:text-foreground data-[selected=true]:border-border-strong"
};

const VERIFICATION_STATE = {
  provider_reported: "data-[selected=true]:bg-secondary data-[selected=true]:text-foreground data-[selected=true]:border-border-strong",
  consultant_observed: "data-[selected=true]:bg-info-soft data-[selected=true]:text-info data-[selected=true]:border-info/40",
  source_required: "data-[selected=true]:bg-warning-soft data-[selected=true]:text-warning data-[selected=true]:border-warning/40",
  source_verified: "data-[selected=true]:bg-success-soft data-[selected=true]:text-success data-[selected=true]:border-success/40"
};

const CHIP_BASE =
  "font-mono-label rounded-sm border border-border bg-card px-2.5 py-1.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:border-border-strong hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40";

const conditionOptions = EVIDENCE_ANSWERS.filter((o) => EVIDENCE_CONDITION_VALUES.includes(o.value));

export function EvidenceMapperRow({ item, value, naReason, evidenceNote, verification, onChange, onReasonChange, onEvidenceNoteChange, onVerificationChange, displayNumber }) {
  const derived =
    value === "na" || value === "unsure"
      ? value
      : EVIDENCE_CONDITION_VALUES.includes(value)
        ? "applicable"
        : null;
  const [markedApplicable, setMarkedApplicable] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const applicability = derived || (markedApplicable ? "applicable" : null);
  const showNote = noteOpen || Boolean(evidenceNote);

  const selectApplicability = (status) => {
    if (status === "applicable") {
      setMarkedApplicable(true);
      if (!EVIDENCE_CONDITION_VALUES.includes(value)) onChange("");
      if (naReason) onReasonChange("");
    } else {
      setMarkedApplicable(false);
      onChange(status);
      if (status !== "na" && naReason) onReasonChange("");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-3 px-5 py-4 md:grid-cols-12 md:items-start md:gap-5">
      <div className="md:col-span-5">
        <div className="flex items-baseline gap-3">
          <span className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            {displayNumber ? String(displayNumber).padStart(2, "0") : String(item.number).padStart(2, "0")}
          </span>
          <h4 className="font-display text-[15px] font-medium leading-tight text-foreground">
            {item.title}
          </h4>
        </div>
        {item.helper && (
          <p className="mt-1 pl-7 text-xs leading-relaxed text-muted-foreground">{item.helper}</p>
        )}
      </div>
      <div className="space-y-3 md:col-span-7">
        <div>
          <p className="font-mono-label mb-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Applicability
          </p>
          <div role="radiogroup" aria-label={`${item.title} applicability`} className="flex flex-wrap gap-1.5">
            {APPLICABILITY_OPTIONS.map((opt) => {
              const selected = applicability === opt.value;
              return (
                <button
                  key={opt.value}
                  role="radio"
                  aria-checked={selected}
                  data-selected={selected}
                  data-testid={`applicability-${item.id}-${opt.value}`}
                  type="button"
                  onClick={() => selectApplicability(opt.value)}
                  className={cn(CHIP_BASE, APPLICABILITY_STATE[opt.value])}
                  title={opt.description}
                >
                  {opt.short}
                </button>
              );
            })}
          </div>
        </div>

        {applicability === "applicable" && (
          <div>
            <p className="font-mono-label mb-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Evidence condition
            </p>
            <div role="radiogroup" aria-label={`${item.title} evidence condition`} className="flex flex-wrap gap-1.5">
              {conditionOptions.map((opt) => {
                const selected = value === opt.value;
                return (
                  <button
                    key={opt.value}
                    role="radio"
                    aria-checked={selected}
                    data-selected={selected}
                    data-testid={`evidence-${item.id}-${opt.value}`}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    className={cn(CHIP_BASE, CONDITION_STATE[opt.value])}
                    title={opt.label}
                  >
                    {opt.short}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {applicability === "applicable" && EVIDENCE_CONDITION_VALUES.includes(value) && (
          <div>
            <p className="font-mono-label mb-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Field verification · supports readiness review only, does not confirm compliance
            </p>
            <div role="radiogroup" aria-label={`${item.title} field verification`} className="flex flex-wrap gap-1.5">
              {FIELD_VERIFICATION_OPTIONS.map((opt) => {
                const selected = (verification || "provider_reported") === opt.value;
                return (
                  <button
                    key={opt.value}
                    role="radio"
                    aria-checked={selected}
                    data-selected={selected}
                    data-testid={`verification-${item.id}-${opt.value}`}
                    type="button"
                    onClick={() => onVerificationChange(opt.value)}
                    className={cn(CHIP_BASE, VERIFICATION_STATE[opt.value])}
                    title={opt.description}
                  >
                    {opt.short}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {(applicability === "na" || applicability === "unsure") && (
          <p
            className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
            data-testid={`verification-fixed-${item.id}`}
          >
            Field verification · {applicability === "na" ? FIELD_VERIFICATION_LABELS.not_applicable : FIELD_VERIFICATION_LABELS.requires_clarification}
          </p>
        )}

        {applicability === "na" && (
          <div>
            <label
              htmlFor={`${item.id}-na-reason`}
              className="font-mono-label mb-1.5 block text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
            >
              Why does this item not apply? · Required
            </label>
            <textarea
              id={`${item.id}-na-reason`}
              data-testid={`na-reason-${item.id}`}
              value={naReason || ""}
              onChange={(e) => onReasonChange(e.target.value)}
              rows={2}
              placeholder="Briefly state why this item is not relevant to this readiness review."
              className="w-full rounded-sm border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            />
          </div>
        )}

        {applicability === "unsure" && (
          <p className="text-xs leading-relaxed text-muted-foreground" data-testid={`clarification-signal-${item.id}`}>
            Recorded as Needs Clarification — this item stays visible as a readiness signal until it is clarified.
          </p>
        )}

        <div>
          {!showNote ? (
            <button
              type="button"
              data-testid={`evidence-note-toggle-${item.id}`}
              onClick={() => setNoteOpen(true)}
              className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              + Add evidence note (optional)
            </button>
          ) : (
            <div>
              <label
                htmlFor={`${item.id}-evidence-note`}
                className="font-mono-label mb-1.5 block text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
              >
                Evidence note · Optional
              </label>
              <textarea
                id={`${item.id}-evidence-note`}
                data-testid={`evidence-note-${item.id}`}
                value={evidenceNote || ""}
                onChange={(e) => onEvidenceNoteChange(e.target.value)}
                rows={2}
                placeholder="What evidence was reviewed, missing, incomplete, unclear, or awaiting confirmation? Do not enter participant or worker identifiers."
                className="w-full rounded-sm border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
